import {onUnmounted} from "vue";
import debounce from "lodash.debounce";
import {useToastStore} from "@/stores/toast.store";
import useAssistantSettings from "@/ai-assistant/composables/useAssistantSettings";
import {AssistantConstraint, AssistantParameterValue, AssistantParameterValues, AssistantSettings} from "@/ai-assistant/interfaces";

/**
 * The parameters of one assistant, edited the way the mockups ask for it:
 * without a save button, each change written straight through.
 *
 * Held apart from any one panel because two screens now edit the same
 * parameters — the copilot's own panel, and the planning rules of the health
 * journey screen, which show a different group of the very same list. The
 * machinery below is what makes writing-through safe, and duplicating it
 * would be duplicating the races it answers.
 *
 * @param assistantKey
 */
const useAssistantSettingsForm = (assistantKey: string) => {
    const {showError} = useToastStore();
    const settingsState = useAssistantSettings(assistantKey);
    const {settings, values, constraints, write, save} = settingsState;

    /**
     * The last state the server confirmed, kept while a burst of edits is
     * pending.
     *
     * Rolling back to the state before the *last* keystroke would not do:
     * several edits collapse into one request, so a refusal has to undo all of
     * them at once — otherwise the earlier ones stay on screen as if they had
     * been saved.
     */
    let confirmed: AssistantSettings | null = null;

    /**
     * Bumped by every edit, so a response can tell whether it is still current.
     */
    let revision = 0;

    /** Whether a request is out. Two at once would land in either order. */
    let saving = false;

    /**
     * Send the form, and keep sending while the pharmacist keeps editing.
     *
     * One request at a time, and the server's answer is only written back when
     * nothing was edited while it travelled. Without that, an edit typed during
     * a save would be wiped by the response of the save that preceded it — and
     * then never sent, because the next request would carry the state that
     * wiped it.
     */
    const push = async () => {
        if (saving) {
            return;
        }

        saving = true;

        try {
            while (true) {
                const sent = revision;
                const rollback = confirmed;

                let served: AssistantSettings | null;

                try {
                    served = await save({values: values.value, typed_constraints: constraints.value});
                } catch {
                    if (rollback) {
                        write(rollback);
                    }

                    confirmed = null;
                    showError({content: "Le paramètre n'a pas pu être enregistré."});

                    return;
                }

                if (revision !== sent) {
                    // Edited while the request was out. What is on screen is
                    // newer than what came back, so it stays, and it is sent in
                    // turn.
                    continue;
                }

                if (served) {
                    write(served);
                    confirmed = served;
                }

                return;
            }
        } finally {
            saving = false;
        }
    };

    /**
     * Debounced because a stepper is pressed repeatedly and a textarea is typed
     * into: one request per keystroke would be both wasteful and racy.
     */
    const persist = debounce(() => void push(), 400);

    // Flushed and not cancelled: the panel is unmounted by the gear button, and
    // a value typed just before collapsing it would otherwise be dropped while
    // the optimistic write keeps showing it as saved.
    onUnmounted(() => persist.flush());

    /**
     * Apply an edit locally, then let it reach the server.
     */
    const edit = (nextValues: AssistantParameterValues, nextConstraints: AssistantConstraint[]) => {
        confirmed ??= settings.value;
        revision++;

        write({...settings.value, values: nextValues, typed_constraints: nextConstraints});
        persist();
    };

    const editValue = (key: string, value: AssistantParameterValue) => {
        edit({...values.value, [key]: value}, constraints.value);
    };

    const editConstraints = (next: AssistantConstraint[]) => {
        edit(values.value, next);
    };

    /**
     * Put the given parameters back to what the server declares, and clear the
     * typed constraints beside them.
     *
     * The caller names the keys it shows, and only those are rewritten: the
     * same assistant's parameters are now edited from two screens, and a reset
     * that dropped the whole settings row would silently undo the groups the
     * pharmacist was not looking at, without telling them.
     *
     * Goes through the ordinary edit path rather than the delete endpoint, so
     * a refusal rolls back like any other change.
     *
     * @param keys
     */
    const restoreDefaults = (keys: string[]) => {
        const next = {...values.value};

        keys.forEach((key) => {
            next[key] = settings.value.defaults[key] ?? null;
        });

        edit(next, []);
    };

    return {
        ...settingsState,
        editValue,
        editConstraints,
        restoreDefaults
    };
};

export default useAssistantSettingsForm;
